import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Sparkle, ArrowsClockwise, FilmStrip, FloppyDisk, Calendar,
  Image as ImageIcon, ChartLineUp, Palette, Lightbulb, Coins, Receipt, Trash,
  Warning, CheckCircle, Funnel, Wrench, Stack, Key, BookmarkSimple, PushPin,
  Question, UploadSimple,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";
import api, { formatApiErrorDetail } from "../../lib/api";
import WorkflowComposer from "./WorkflowComposer";
import { ModelsRegistryPanel, ProviderKeysPanel } from "./WorkflowSettings";
import TimelineEditor from "./TimelineEditor";
import { CostGuideModal } from "./CostChip";
import SystemHealthPill from "./SystemHealthPill";
import { assetUrl } from "../../lib/assetUrl";
import { SystemHealthProvider } from "../../context/SystemHealthContext";
import LibraryUploadModal from "./LibraryUploadModal";

// Storyboard composer · Brand kit · Weekly schedule · Next-day learning loop.
export default function StoryboardPanel() {
  const [subTab, setSubTab] = useState("workflow");
  const [errorCount, setErrorCount] = useState(0);
  const [showCostGuide, setShowCostGuide] = useState(false);

  // Lightweight badge — refresh the unresolved-error count every 30s.
  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      api.get("/admin/social/error-log?resolved=false&limit=1")
        .then(({ data }) => { if (!cancelled) setErrorCount(data?.counts?.unresolved || 0); })
        .catch(() => {});
    };
    refresh();
    const t = setInterval(refresh, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [subTab]);

  return (
    <SystemHealthProvider>
    <div className="space-y-5" data-testid="admin-storyboard-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="grid sm:grid-cols-2 gap-3 flex-1 min-w-0">
          <CostBanner onClick={() => setSubTab("cost")} />
          <LibraryUsagePill onClick={() => setSubTab("library")} />
        </div>
        <div className="inline-flex items-center gap-2 self-start shrink-0">
          <SystemHealthPill />
          <button onClick={() => setShowCostGuide(true)}
            className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5"
            title="Open the live cost guide — per-stage rates, 5-scene recipe totals, library-savings, daily cap, top-up steps."
            data-testid="storyboard-cost-guide-open">
            <Question size={14} weight="duotone" /> Cost guide
          </button>
        </div>
      </div>
      <div className="flex gap-1 border-b hairline flex-wrap">
        {[
          ["workflow", "Workflow",             Stack,        null],
          ["compose",  "Legacy composer",       FilmStrip,    null],
          ["library",  "Library",               BookmarkSimple, null],
          ["brand",    "Brand kit",             Palette,      null],
          ["models",   "Models registry",       Wrench,       null],
          ["keys",     "Provider keys",         Key,          null],
          ["schedule", "7am IST schedule",      Calendar,     null],
          ["learning", "Analytics & learning",  ChartLineUp,  null],
          ["cost",     "Cost dashboard",        Coins,        null],
          ["errors",   "Error log",             Warning,      errorCount > 0 ? errorCount : null],
        ].map(([id, label, Icon, badge]) => (
          <button
            key={id}
            onClick={() => setSubTab(id)}
            data-testid={`storyboard-subtab-${id}`}
            className={`px-3 py-2 text-xs -mb-px border-b-2 inline-flex items-center gap-1.5 ${subTab === id ? "border-[hsl(var(--primary))] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <Icon size={14} weight="duotone" /> {label}
            {badge != null && (
              <span
                className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-medium bg-destructive text-white"
                data-testid="storyboard-error-count-badge"
              >
                {badge}
              </span>
            )}
          </button>
        ))}
      </div>
      {subTab === "workflow" && (
        <WorkflowComposer
          onJumpToDraft={(id) => {
            try {
              const url = new URL(window.location.href);
              url.searchParams.set("storyboardDraftId", id);
              window.history.replaceState({}, "", url.toString());
            } catch (_) { /* ignore */ }
            setSubTab("compose");
          }}
        />
      )}
      {subTab === "compose"  && <Composer />}
      {subTab === "library"  && <LibraryPanel />}
      {subTab === "brand"    && <BrandKitEditor />}
      {subTab === "models"   && <ModelsRegistryPanel />}
      {subTab === "keys"     && <ProviderKeysPanel />}
      {subTab === "schedule" && <Schedule />}
      {subTab === "learning" && <Learning />}
      {subTab === "cost"     && <CostDashboard />}
      {subTab === "errors"   && <ErrorLog />}
      {showCostGuide && <CostGuideModal onClose={() => setShowCostGuide(false)} />}
    </div>
    </SystemHealthProvider>
  );
}

/* -------------------- Composer -------------------- */
function Composer() {
  const [prompt, setPrompt] = useState("");
  const [sceneCount, setSceneCount] = useState(5);
  const [autoGen, setAutoGen] = useState(true);
  const [busy, setBusy] = useState(false);
  // Allow `?storyboardDraftId=xyz` to auto-load a previous draft into the
  // composer workspace — saves the admin from rerunning the whole pipeline
  // just to view / retry an existing draft.
  const initialDraftId = (() => {
    if (typeof window === "undefined") return null;
    const sp = new URLSearchParams(window.location.search);
    return sp.get("storyboardDraftId");
  })();
  const [draftId, setDraftId] = useState(initialDraftId);
  const [draft, setDraft] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [costSummary, setCostSummary] = useState(null);
  const pollRef = useRef(null);

  // Live cost preview — refetched whenever scene_count changes (debounced).
  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/admin/social/cost-summary?scene_count=${parseInt(sceneCount, 10) || 1}`)
        .then(({ data }) => setCostSummary(data))
        .catch(() => setCostSummary(null));
    }, 200);
    return () => clearTimeout(t);
  }, [sceneCount]);

  const loadDraft = useCallback(async (id) => {
    try {
      const [d, sc] = await Promise.all([
        api.get(`/admin/social/drafts/${id}`),
        api.get(`/admin/social/drafts/${id}/scenes`),
      ]);
      setDraft(d.data);
      setScenes(sc.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    if (!draftId) return undefined;
    loadDraft(draftId);
    // Poll while assets generate.
    pollRef.current = setInterval(() => loadDraft(draftId), 5000);
    return () => clearInterval(pollRef.current);
  }, [draftId, loadDraft]);

  const submit = async () => {
    if (prompt.trim().length < 4) { toast.error("Type a short hook first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/social/storyboard", {
        prompt: prompt.trim(),
        scene_count: parseInt(sceneCount, 10),
        auto_generate_assets: autoGen,
      });
      setDraftId(data.draft_id);
      toast.success(autoGen
        ? "Scripts ready — Sora 2 video + Gemini images render in background (3–8 min)."
        : "Scripts ready. Generate assets per-scene when you're happy.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="card-flat p-6">
        <div className="eyebrow mb-2">Storyboard composer</div>
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          One short prompt → Claude writes a scene-by-scene script, Gemini renders a brand-consistent
          illustration for each scene, Sora 2 produces a 6–8 sec clip per scene, and ffmpeg stitches
          the timeline with your brand kit overlaid. Per-platform captions (FB · Insta · WhatsApp ·
          LinkedIn · YouTube) and an SEO article for the /insights mirror are produced in the same pass.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="text-sm block sm:col-span-2">
            <span className="block text-xs text-muted-foreground mb-1">Short hook / topic</span>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={2}
              placeholder="e.g. How a 30-something Bengaluru engineer decides between renting in Whitefield vs buying a Rs 1.2 Cr flat in Yelahanka."
              className="w-full bg-background border hairline px-3 py-2 text-sm"
              data-testid="storyboard-prompt"
            />
          </label>
          <label className="text-sm block">
            <span className="block text-xs text-muted-foreground mb-1">Number of scenes</span>
            <input
              type="number"
              min={2}
              max={10}
              value={sceneCount}
              onChange={(e) => setSceneCount(e.target.value)}
              className="bg-background border hairline px-3 py-2 text-sm w-24"
              data-testid="storyboard-scene-count"
            />
          </label>
          <label className="text-sm inline-flex items-center gap-2 self-end">
            <input
              type="checkbox"
              checked={autoGen}
              onChange={(e) => setAutoGen(e.target.checked)}
              data-testid="storyboard-autogen"
            />
            <span>Auto-render images + videos (uncheck to script only)</span>
          </label>
        </div>
        <div className="flex justify-between items-end mt-5 flex-wrap gap-3">
          <CostPreview summary={costSummary} autoGen={autoGen} />
          <button
            onClick={submit}
            disabled={busy || (autoGen && costSummary?.would_exceed)}
            className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="storyboard-submit"
          >
            <Sparkle size={14} weight="duotone" /> {busy ? "Generating…" : "Generate storyboard"}
          </button>
        </div>
      </div>

      {draftId && draft && (
        <>
          {draft.status === "error" && draft.error && (
            <div className="card-flat p-4 border-l-4 border-destructive bg-destructive/5 text-sm" data-testid="storyboard-error">
              <div className="font-medium text-destructive mb-1">Storyboard generation failed</div>
              <div className="text-muted-foreground">{draft.error}</div>
              <div className="text-[10px] text-muted-foreground mt-2">
                Open the <b>Error log</b> sub-tab above for the exact stage that failed, the model
                involved, and a one-line actionable fix.
              </div>
            </div>
          )}
          <StoryboardWorkspace draft={draft} scenes={scenes} draftId={draftId} reload={() => loadDraft(draftId)} />
        </>
      )}
    </div>
  );
}

function StoryboardWorkspace({ draft, scenes, draftId, reload }) {
  const seo = draft.seo || {};
  const captions = draft.platform_captions || {};
  const [retryPreview, setRetryPreview] = useState(null);
  const [retrying, setRetrying] = useState(false);

  // Detect which scenes need a retry (no video OR error state). Stays in sync
  // with the same `_scene_needs_retry` logic on the backend.
  const sceneNeedsRetry = (s) =>
    !s.video_url || ["error", "video_error", "scripted", "retrying"].includes(s.status);
  const failedSceneCount = scenes.filter(sceneNeedsRetry).length;

  useEffect(() => {
    if (failedSceneCount === 0) { setRetryPreview(null); return; }
    let cancelled = false;
    api.get(`/admin/social/drafts/${draftId}/retry-preview`)
      .then(({ data }) => { if (!cancelled) setRetryPreview(data); })
      .catch(() => { if (!cancelled) setRetryPreview(null); });
    return () => { cancelled = true; };
  }, [draftId, failedSceneCount]);

  const triggerRetry = async () => {
    if (retrying || !retryPreview) return;
    const msg =
      `Retry ${retryPreview.retry_count} failed scene${retryPreview.retry_count === 1 ? "" : "s"} ` +
      `at an estimated ₹${retryPreview.estimated_cost_inr}? ` +
      `(Today's spend so far: ₹${retryPreview.today_spent_inr} / cap ₹${retryPreview.daily_cap_inr})`;
    if (!window.confirm(msg)) return;
    setRetrying(true);
    try {
      const { data } = await api.post(`/admin/social/drafts/${draftId}/retry-failed`);
      toast.success(`Retrying ${data.retry_count} scene${data.retry_count === 1 ? "" : "s"} · ₹${data.estimated_cost_inr} budget`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="card-flat p-5" data-testid="storyboard-draft-summary">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="eyebrow">Draft · {draft.status?.toUpperCase()}</div>
            <div className="font-serif text-xl mt-1 truncate max-w-2xl">{draft.topic}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {failedSceneCount > 0 && retryPreview && (
              <button
                onClick={triggerRetry}
                disabled={retrying || retryPreview.would_exceed_daily_cap}
                className="px-3 py-1.5 text-xs inline-flex items-center gap-1.5 bg-[hsl(var(--primary))] text-white rounded hover:opacity-90 disabled:opacity-50"
                data-testid="storyboard-retry-failed"
                title={retryPreview.would_exceed_daily_cap
                  ? `Daily cap ₹${retryPreview.daily_cap_inr} would be breached.`
                  : `Re-runs image + video for ${retryPreview.retry_count} scene${retryPreview.retry_count === 1 ? "" : "s"} only`}
              >
                <ArrowsClockwise size={12} weight="duotone" />
                {retrying
                  ? "Retrying…"
                  : `Retry ${retryPreview.retry_count} failed scene${retryPreview.retry_count === 1 ? "" : "s"} · ₹${retryPreview.estimated_cost_inr}`}
              </button>
            )}
            <button
              onClick={async () => {
                try {
                  await api.post(`/admin/social/drafts/${draftId}/merge`);
                  toast.success("Timeline re-stitched.");
                  reload();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail));
                }
              }}
              className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1"
              data-testid="storyboard-merge"
            >
              <FilmStrip size={12} weight="duotone" /> Re-stitch timeline
            </button>
          </div>
        </div>
        {failedSceneCount > 0 && retryPreview && (
          <div
            className="text-[11px] text-muted-foreground mb-3 -mt-1 inline-flex items-start gap-1.5 max-w-2xl"
            data-testid="storyboard-retry-hint"
          >
            <Lightbulb size={12} weight="duotone" className="shrink-0 mt-0.5 text-[hsl(var(--secondary))]" />
            <span>
              {failedSceneCount} scene{failedSceneCount === 1 ? "" : "s"} missing video.
              {" "}Use <b>Retry from last good scene</b> to re-run only the failed ones —
              saves ₹{Math.max(0, (scenes.length - failedSceneCount) * (retryPreview.per_scene_inr || 0)).toFixed(0)} vs regenerating the whole storyboard.
            </span>
          </div>
        )}
        {draft.video_url && (
          <video src={draft.video_url} controls className="w-full max-h-80 border hairline" data-testid="storyboard-video" />
        )}
        {seo.slug && (
          <div className="text-[11px] text-muted-foreground mt-2">
            SEO mirror:{" "}
            <a href={`/insights/${seo.slug}`} target="_blank" rel="noreferrer" className="text-[hsl(var(--secondary))] hover:underline">
              /insights/{seo.slug}
            </a>
          </div>
        )}
      </div>

      {/* Phase 2.A — drag-drop timeline editor (reorder, exclude, trim, text, music) */}
      {scenes.length > 0 && (
        <TimelineEditor draft={draft} scenes={scenes} draftId={draftId} reload={reload} />
      )}

      <div className="space-y-4">
        <div className="eyebrow">Scenes ({scenes.length})</div>
        {scenes.map((s) => (
          <SceneCard key={s.id} scene={s} reload={reload} />
        ))}
      </div>

      {Object.keys(captions).length > 0 && (
        <div className="card-flat p-5" data-testid="storyboard-platform-captions">
          <div className="eyebrow mb-3">Per-platform captions</div>
          <div className="grid lg:grid-cols-2 gap-4">
            {Object.entries(captions).map(([platform, c]) => (
              <PlatformCaptionCard key={platform} platform={platform} content={c} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SceneCard({ scene, reload }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    title: scene.title,
    script: scene.script,
    image_prompt: scene.image_prompt,
    video_prompt: scene.video_prompt,
    duration: scene.duration,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/social/scenes/${scene.id}`, draft);
      toast.success("Scene saved.");
      setEditing(false);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/social/scenes/${scene.id}/regenerate`);
      toast.success("Re-rendering image + Sora 2 video (1–3 min).");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="card-flat p-4 grid md:grid-cols-[140px_1fr] gap-4" data-testid={`storyboard-scene-${scene.index}`}>
      <div className="space-y-2">
        <div className="aspect-video bg-[hsl(var(--muted))] flex items-center justify-center overflow-hidden">
          {scene.image_url ? (
            <img src={assetUrl(scene.image_url)} alt={`Scene ${scene.index}`} className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={28} className="text-muted-foreground/40" />
          )}
        </div>
        {scene.video_url ? (
          <video src={scene.video_url} controls className="w-full" />
        ) : (
          <div className="text-[10px] text-center text-muted-foreground py-2 bg-[hsl(var(--muted))]">
            {scene.status === "regenerating" || scene.status === "scripted" ? "rendering…" : scene.status}
          </div>
        )}
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Scene {scene.index}</span>
            <div className="font-serif text-lg">{editing ? (
              <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="bg-background border hairline px-2 py-1 text-sm w-full" />
            ) : scene.title}</div>
          </div>
          <div className="flex gap-1.5">
            {editing ? (
              <>
                <button onClick={() => setEditing(false)} className="btn-ghost px-2 py-1 text-xs">Cancel</button>
                <button onClick={save} disabled={busy} className="btn-primary px-2 py-1 text-xs inline-flex items-center gap-1">
                  <FloppyDisk size={12} weight="duotone" /> Save
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setEditing(true)} className="btn-ghost px-2 py-1 text-xs" data-testid={`scene-${scene.index}-edit`}>Edit</button>
                <button onClick={regenerate} disabled={busy} className="btn-ghost px-2 py-1 text-xs inline-flex items-center gap-1" data-testid={`scene-${scene.index}-regen`}>
                  <ArrowsClockwise size={12} weight="duotone" /> Regenerate
                </button>
              </>
            )}
          </div>
        </div>
        {editing ? (
          <>
            <Field label="Script / voice-over" value={draft.script} onChange={(v) => setDraft({ ...draft, script: v })} rows={3} />
            <Field label="Image prompt (Gemini Nano Banana)" value={draft.image_prompt} onChange={(v) => setDraft({ ...draft, image_prompt: v })} rows={2} />
            <Field label="Video prompt (Sora 2)" value={draft.video_prompt} onChange={(v) => setDraft({ ...draft, video_prompt: v })} rows={2} />
            <label className="block text-xs">
              <span className="block text-muted-foreground mb-1">Duration (sec, 4/8/12)</span>
              <input type="number" min={4} max={12} value={draft.duration} onChange={(e) => setDraft({ ...draft, duration: parseInt(e.target.value || "6", 10) })} className="bg-background border hairline px-2 py-1 text-sm w-24" />
            </label>
          </>
        ) : (
          <>
            <p className="text-xs whitespace-pre-wrap leading-relaxed bg-[hsl(var(--muted))] p-2">{scene.script}</p>
            <details className="text-[11px] text-muted-foreground">
              <summary className="cursor-pointer">Prompts</summary>
              <div className="mt-1">
                <div><span className="uppercase tracking-wider">Image:</span> {scene.image_prompt}</div>
                <div><span className="uppercase tracking-wider">Video:</span> {scene.video_prompt}</div>
              </div>
            </details>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, rows = 2 }) {
  return (
    <label className="block text-xs">
      <span className="block text-muted-foreground mb-1">{label}</span>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="w-full bg-background border hairline px-2 py-1.5 text-sm" />
    </label>
  );
}

// Pre-flight cost preview — shows estimated INR for this draft + today's
// cumulative spend against the daily cap. Renders nothing when the API hasn't
// answered yet (no flash of empty state).
function CostPreview({ summary, autoGen }) {
  if (!summary) return <div className="text-[10px] text-muted-foreground">Computing cost…</div>;
  const est = summary.estimate || {};
  const spent = summary.today_spent_inr;
  const cap = summary.daily_cap_inr;
  const remaining = summary.cap_remaining_inr;
  const willExceed = summary.would_exceed;
  const tone = willExceed ? "text-destructive" : (spent / cap > 0.7 ? "text-[#B89B72]" : "text-muted-foreground");
  return (
    <div className={`text-[11px] leading-snug max-w-md ${tone}`} data-testid="storyboard-cost-preview">
      <div>
        Estimated <span className="num-metric text-foreground">₹{est.total_inr?.toLocaleString("en-IN")}</span>
        {" "}({est.scene_count} scenes ·{" "}
        {est.lines?.filter((l) => l.inr > 0).map((l) => `₹${l.inr}`).join(" + ")})
      </div>
      <div>
        Today: <span className="num-metric">₹{spent.toLocaleString("en-IN")}</span> / cap{" "}
        <span className="num-metric">₹{cap.toLocaleString("en-IN")}</span>
        {" · "}remaining <span className="num-metric">₹{remaining.toLocaleString("en-IN")}</span>
      </div>
      {autoGen && willExceed && (
        <div className="text-destructive font-medium mt-1" data-testid="storyboard-cost-block">
          Would breach the daily ceiling — raise the cap in Brand kit or wait until tomorrow.
        </div>
      )}
      {!autoGen && (
        <div className="text-muted-foreground mt-1">
          Auto-render is off — only the Claude script (₹{est.lines?.find((l) => l.label.includes("Claude"))?.inr ?? 0}) will run; the rest applies when you regenerate scenes.
        </div>
      )}
    </div>
  );
}

function PlatformCaptionCard({ platform, content }) {
  const copy = (s) => { navigator.clipboard.writeText(s || ""); toast.success("Copied."); };
  const title = platform[0].toUpperCase() + platform.slice(1);
  return (
    <div className="border hairline p-3 space-y-2" data-testid={`platform-caption-${platform}`}>
      <div className="flex items-baseline justify-between">
        <div className="text-sm font-medium">{title}</div>
        <button onClick={() => copy(content.caption || content.description)} className="text-[10px] text-[hsl(var(--secondary))] hover:underline">Copy</button>
      </div>
      {content.title && <div className="text-xs font-semibold">{content.title}</div>}
      <div className="text-xs whitespace-pre-wrap bg-[hsl(var(--muted))] p-2 max-h-48 overflow-y-auto">
        {content.caption || content.description || "—"}
      </div>
      {(content.hashtags || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {content.hashtags.map((h) => (
            <span key={h} className="text-[10px] bg-[hsl(var(--muted))] px-1.5 py-0.5 rounded-sm">#{h}</span>
          ))}
        </div>
      )}
      {(content.chapters || []).length > 0 && (
        <div className="text-[10px] text-muted-foreground">
          <div className="uppercase tracking-wider mb-0.5">Chapters</div>
          {content.chapters.map((c, i) => {
            // YouTube chapters come as {timestamp, title} objects; older
            // captions may have plain strings — handle both safely.
            if (typeof c === "string") return <div key={`chapter-${c}-${i}`}>{c}</div>;
            if (c && typeof c === "object") {
              const ts = c.timestamp || c.time || "";
              const ttl = c.title || c.label || "";
              return <div key={`${ts}-${ttl}-${i}`}>{ts ? `${ts} · ${ttl}` : ttl}</div>;
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
}

/* -------------------- Brand kit editor -------------------- */
function BrandKitEditor() {
  const [kit, setKit] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/social/brand-kit").then(({ data }) => setKit(data)).catch((e) => {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/social/brand-kit", kit);
      toast.success("Brand kit saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!kit) return <div className="text-muted-foreground text-sm">Loading…</div>;
  const up = (k, v) => setKit({ ...kit, [k]: v });

  return (
    <div className="card-flat p-6 space-y-4" data-testid="brand-kit-editor">
      <div>
        <div className="eyebrow">Brand kit</div>
        <p className="text-xs text-muted-foreground max-w-2xl mt-1">
          Used by every render: the watermark + scene title are burned into each Sora 2 clip via ffmpeg drawtext,
          and the illustration style is appended to every Gemini Nano Banana prompt so the same character / look
          appears across scenes. Same `character_seed` keeps consistency across the whole storyboard.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <BField label="Watermark text" value={kit.watermark_text} onChange={(v) => up("watermark_text", v)} />
        <BField label="Logo URL (optional)" value={kit.logo_url || ""} onChange={(v) => up("logo_url", v)} />
        <BField label="Primary color (#hex)" value={kit.primary_color} onChange={(v) => up("primary_color", v)} />
        <BField label="Secondary color (#hex)" value={kit.secondary_color} onChange={(v) => up("secondary_color", v)} />
        <BField label="Font family" value={kit.font_family} onChange={(v) => up("font_family", v)} />
        <BField label="Character seed (illustration consistency)" value={kit.character_seed} onChange={(v) => up("character_seed", parseInt(v || "0", 10))} type="number" />
        <label className="text-sm block sm:col-span-2">
          <span className="block text-xs text-muted-foreground mb-1">Illustration style (appended to every image prompt)</span>
          <textarea
            rows={2}
            value={kit.illustration_style}
            onChange={(e) => up("illustration_style", e.target.value)}
            className="w-full bg-background border hairline px-3 py-2 text-sm"
          />
        </label>
      </div>

      <div className="border-t hairline pt-4 mt-2">
        <div className="eyebrow mb-2">Cost guardrails</div>
        <p className="text-[11px] text-muted-foreground max-w-2xl mb-3">
          Per-unit INR estimates used by the Composer's cost preview AND enforced as a hard
          server-side ceiling. Tune as Sora 2 / Gemini / Claude pricing shifts. HeyGen rate is
          plumbed for when the avatar integration lands; leave 0 until then.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <BField label="Sora 2 per clip (₹)" type="number" value={kit.sora2_inr_per_clip ?? 60} onChange={(v) => up("sora2_inr_per_clip", Number(v))} />
          <BField label="Gemini image per gen (₹)" type="number" value={kit.gemini_image_inr_per_gen ?? 8} onChange={(v) => up("gemini_image_inr_per_gen", Number(v))} />
          <BField label="Claude per storyboard (₹)" type="number" value={kit.claude_inr_per_storyboard ?? 12} onChange={(v) => up("claude_inr_per_storyboard", Number(v))} />
          <BField label="HeyGen per minute (₹)" type="number" value={kit.heygen_inr_per_min ?? 0} onChange={(v) => up("heygen_inr_per_min", Number(v))} />
          <BField label="Per-draft cap (₹)" type="number" value={kit.per_draft_inr_cap ?? 400} onChange={(v) => up("per_draft_inr_cap", Number(v))} />
          <BField label="Daily cap (₹)" type="number" value={kit.daily_inr_cap ?? 2000} onChange={(v) => up("daily_inr_cap", Number(v))} />
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-1">
          <FloppyDisk size={12} weight="duotone" /> {saving ? "Saving…" : "Save brand kit"}
        </button>
      </div>
    </div>
  );
}

function BField({ label, value, onChange, type = "text" }) {
  return (
    <label className="text-sm block">
      <span className="block text-xs text-muted-foreground mb-1">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-background border hairline px-3 py-2 text-sm"
      />
    </label>
  );
}

/* -------------------- Schedule -------------------- */
function Schedule() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [date, setDate] = useState(() => {
    const d = new Date(Date.now() + 86400000);
    return d.toISOString().slice(0, 10);
  });
  const [draftId, setDraftId] = useState("");
  const [platforms, setPlatforms] = useState({ facebook: true, instagram: true, linkedin: true, youtube: false, whatsapp: false });
  const [autoApprove, setAutoApprove] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([
        api.get("/admin/social/schedule"),
        api.get("/admin/social/drafts"),
      ]);
      setItems(s.data);
      setDrafts(d.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!draftId) { toast.error("Pick a draft."); return; }
    const platforms_arr = Object.entries(platforms).filter(([_k, v]) => v).map(([k]) => k);
    try {
      await api.post("/admin/social/schedule", {
        publish_date: date,
        draft_id: draftId,
        platforms: platforms_arr,
        auto_approve: autoApprove,
      });
      toast.success(`Scheduled for ${date} 7am IST.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove the schedule for ${d}?`)) return;
    await api.delete(`/admin/social/schedule/${d}`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="storyboard-schedule">
      <div className="card-flat p-6">
        <div className="eyebrow mb-2">7am IST schedule</div>
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          Pre-approve a weekly content calendar. The 7am IST cron picks up any approved (or
          auto-approve) entry for that date and publishes it (or marks it draft-copied if a
          platform token is missing — see Social Connections).
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm block">
            <span className="block text-xs text-muted-foreground mb-1">Publish date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-background border hairline px-3 py-2 text-sm" data-testid="schedule-date" />
          </label>
          <label className="text-sm block">
            <span className="block text-xs text-muted-foreground mb-1">Draft</span>
            <select value={draftId} onChange={(e) => setDraftId(e.target.value)} className="w-full bg-background border hairline px-3 py-2 text-sm" data-testid="schedule-draft">
              <option value="">— pick draft —</option>
              {drafts.map((d) => <option key={d.id} value={d.id}>{d.topic} · {d.status}</option>)}
            </select>
          </label>
          <div className="text-sm sm:col-span-2">
            <div className="text-xs text-muted-foreground mb-1">Platforms</div>
            <div className="flex flex-wrap gap-2">
              {Object.keys(platforms).map((p) => (
                <label key={p} className="text-xs inline-flex items-center gap-1.5 border hairline px-2 py-1 cursor-pointer">
                  <input type="checkbox" checked={platforms[p]} onChange={(e) => setPlatforms({ ...platforms, [p]: e.target.checked })} />
                  <span>{p}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="text-sm inline-flex items-center gap-2 self-end">
            <input type="checkbox" checked={autoApprove} onChange={(e) => setAutoApprove(e.target.checked)} data-testid="schedule-auto-approve" />
            <span>Auto-approve (publishes without my manual click)</span>
          </label>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={submit} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-1" data-testid="schedule-submit">
            <Calendar size={12} weight="duotone" /> Schedule
          </button>
        </div>
      </div>

      <div className="card-flat p-5" data-testid="schedule-list">
        <div className="eyebrow mb-3">Upcoming ({items.length})</div>
        {items.length === 0 && <div className="text-sm text-muted-foreground">No items scheduled yet.</div>}
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between border-b hairline last:border-b-0 py-2 text-sm flex-wrap gap-2">
            <div className="min-w-0">
              <div className="font-medium">{it.publish_date}</div>
              <div className="text-[10px] text-muted-foreground">{(it.platforms || []).join(" · ")}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] uppercase tracking-wider ${it.published_at ? "text-[hsl(var(--secondary))]" : it.approved ? "text-foreground" : "text-muted-foreground"}`}>
                {it.published_at ? "published" : it.approved ? "approved" : "pending"}
              </span>
              <button onClick={() => remove(it.publish_date)} className="btn-ghost px-2 py-1 text-[10px]" data-testid={`schedule-remove-${it.publish_date}`}>Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------- Analytics & next-day learning -------------------- */
function Learning() {
  const [data, setData] = useState(null);
  const [spend, setSpend] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [form, setForm] = useState({ draft_id: "", platform: "facebook", impressions: 0, likes: 0, comments: 0, shares: 0, clicks: 0 });

  const load = useCallback(async () => {
    try {
      const [l, d, s] = await Promise.all([
        api.get("/admin/social/learning"),
        api.get("/admin/social/drafts"),
        api.get("/admin/social/spend-series?days=14"),
      ]);
      setData(l.data);
      setDrafts(d.data);
      setSpend(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.draft_id) { toast.error("Pick a draft."); return; }
    try {
      await api.post("/admin/social/metrics", { ...form, impressions: parseInt(form.impressions, 10), likes: parseInt(form.likes, 10), comments: parseInt(form.comments, 10), shares: parseInt(form.shares, 10), clicks: parseInt(form.clicks, 10) });
      toast.success("Metrics captured — tomorrow's auto-picker will use them.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="storyboard-learning">
      {spend && <SpendChart spend={spend} title="14-day AI spend (Claude · Gemini · Sora 2 · HeyGen)" series="llm" />}
      <div className="card-flat p-6">
        <div className="eyebrow mb-2 inline-flex items-center gap-2">
          <Lightbulb size={14} weight="duotone" className="text-[hsl(var(--secondary))]" /> Next-day topic bias
        </div>
        {data ? (
          <>
            <p className="text-sm">
              Suggested for tomorrow's auto-storyboard:{" "}
              <span className="text-[hsl(var(--secondary))]">{data.suggested_topic_slug}</span>
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">{data.note}</p>
            {(data.ranked || []).length > 0 && (
              <table className="w-full text-xs mt-3">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left p-1.5">Slug</th>
                    <th className="text-right p-1.5">Impressions</th>
                    <th className="text-right p-1.5">Engagements</th>
                    <th className="text-right p-1.5">Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ranked.map((r) => (
                    <tr key={r.slug} className="border-t hairline">
                      <td className="p-1.5">{r.slug}</td>
                      <td className="text-right p-1.5">{r.impressions}</td>
                      <td className="text-right p-1.5">{r.engagements}</td>
                      <td className="text-right p-1.5">{r.clicks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        ) : <div className="text-sm text-muted-foreground">Loading…</div>}
      </div>

      <div className="card-flat p-6">
        <div className="eyebrow mb-2">Paste yesterday's platform metrics</div>
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          Until live OAuth scraping ships, paste impressions / engagements from each platform's
          dashboard so the topic-picker can learn. Clicks landing on /insights/&lt;slug&gt; are tallied
          automatically.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="text-sm block sm:col-span-2">
            <span className="block text-xs text-muted-foreground mb-1">Draft</span>
            <select value={form.draft_id} onChange={(e) => setForm({ ...form, draft_id: e.target.value })} className="w-full bg-background border hairline px-3 py-2 text-sm" data-testid="metrics-draft">
              <option value="">— pick draft —</option>
              {drafts.map((d) => <option key={d.id} value={d.id}>{d.topic}</option>)}
            </select>
          </label>
          <label className="text-sm block">
            <span className="block text-xs text-muted-foreground mb-1">Platform</span>
            <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="w-full bg-background border hairline px-3 py-2 text-sm" data-testid="metrics-platform">
              {["facebook", "instagram", "whatsapp", "linkedin", "youtube"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          {["impressions", "likes", "comments", "shares", "clicks"].map((k) => (
            <label key={k} className="text-sm block">
              <span className="block text-xs text-muted-foreground mb-1">{k}</span>
              <input
                type="number"
                min={0}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="w-full bg-background border hairline px-3 py-2 text-sm"
                data-testid={`metrics-${k}`}
              />
            </label>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={submit} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-1" data-testid="metrics-submit">
            <FloppyDisk size={12} weight="duotone" /> Save metrics
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Spend chart (shared by Learning + Cost dashboard) -------------------- */
const LLM_COLORS = {
  claude_sonnet_4_5: "#C85A32",
  gemini_nano_banana: "#5B7CFA",
  sora2: "#B89B72",
  heygen: "#5FB37C",
};
const EXTERNAL_COLORS = {
  emergent_platform: "#C85A32",
  domain: "#5B7CFA",
  resend_email: "#B89B72",
  razorpay_fees: "#5FB37C",
  facebook_api: "#3b5998",
  instagram_api: "#E1306C",
  whatsapp_api: "#25D366",
  linkedin_api: "#0077B5",
  youtube_api: "#FF0000",
  other: "#8895a8",
};

function SpendChart({ spend, title, series = "llm" }) {
  const data = series === "llm" ? spend.llm_series : spend.external_series;
  const cats = series === "llm" ? spend.categories_llm : spend.categories_external;
  // Trim trailing zeros from category list (keeps the legend clean).
  const liveCats = cats.filter((c) => data.some((d) => (d[c] || 0) > 0));
  return (
    <div className="card-flat p-5" data-testid={`spend-chart-${series}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="eyebrow">{title}</div>
        <div className="text-[10px] text-muted-foreground">{data.length} days · IST</div>
      </div>
      {liveCats.length === 0 ? (
        <div className="text-xs text-muted-foreground py-6 text-center">No spend logged in this window yet.</div>
      ) : (
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v}`} />
              <Tooltip
                contentStyle={{ background: "#0A0908", border: "1px solid #333", fontSize: 11 }}
                formatter={(v, k) => [`₹${Number(v).toLocaleString("en-IN")}`, k]}
              />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {liveCats.map((c) => (
                <Bar
                  key={c}
                  dataKey={c}
                  stackId="a"
                  fill={(series === "llm" ? LLM_COLORS : EXTERNAL_COLORS)[c] || "#8895a8"}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

/* -------------------- Cost dashboard (full breakdown) -------------------- */
function CostDashboard() {
  const [spend, setSpend] = useState(null);
  const [costs, setCosts] = useState([]);
  const [form, setForm] = useState({ category: "emergent_platform", amount_inr: "", note: "", incurred_on: new Date().toISOString().slice(0, 10) });

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/social/spend-series?days=14"),
        api.get("/admin/social/external-cost"),
      ]);
      setSpend(s.data);
      setCosts(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const amt = Number(form.amount_inr);
    if (!amt || amt <= 0) { toast.error("Enter a positive ₹ amount."); return; }
    try {
      await api.post("/admin/social/external-cost", { ...form, amount_inr: amt });
      toast.success("Cost logged.");
      setForm({ ...form, amount_inr: "", note: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this cost entry?")) return;
    await api.delete(`/admin/social/external-cost/${id}`);
    load();
  };

  if (!spend) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const llmTotal = Object.values(spend.totals_llm).reduce((a, b) => a + b, 0);
  const externalTotal = Object.values(spend.totals_external).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-5" data-testid="cost-dashboard">
      <div className="grid sm:grid-cols-3 gap-3">
        <KpiCard label="Total spend (lifetime)" value={`₹${Number(spend.totals_grand).toLocaleString("en-IN")}`} tone="primary" />
        <KpiCard label="AI bill (LLM + video)" value={`₹${llmTotal.toLocaleString("en-IN")}`} />
        <KpiCard label="Platform & infra" value={`₹${externalTotal.toLocaleString("en-IN")}`} />
      </div>

      <SpendChart spend={spend} title="14-day AI spend (Claude · Gemini · Sora 2 · HeyGen)" series="llm" />
      <SpendChart spend={spend} title="14-day platform & API spend (Emergent · domain · Resend · FB · Insta · WhatsApp · LinkedIn · YouTube · Razorpay)" series="external" />

      <div className="card-flat p-5" data-testid="cost-log-form">
        <div className="eyebrow mb-3 inline-flex items-center gap-2"><Receipt size={14} weight="duotone" /> Log a non-AI cost</div>
        <p className="text-[11px] text-muted-foreground mb-3 max-w-2xl">
          Domain renewal · Emergent platform credits · Resend / SendGrid invoices · Meta Page boost spend
          · WhatsApp Cloud API per-message charges · Razorpay payment-gateway fees. The dashboard
          totals every line so you have one number for "what does Estima cost to run?".
        </p>
        <div className="grid sm:grid-cols-5 gap-3">
          <label className="text-sm block sm:col-span-2">
            <span className="block text-xs text-muted-foreground mb-1">Category</span>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-background border hairline px-3 py-2 text-sm" data-testid="cost-category">
              {spend.categories_external.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
            </select>
          </label>
          <label className="text-sm block">
            <span className="block text-xs text-muted-foreground mb-1">Amount (₹)</span>
            <input type="number" min={1} value={form.amount_inr} onChange={(e) => setForm({ ...form, amount_inr: e.target.value })} className="w-full bg-background border hairline px-3 py-2 text-sm" data-testid="cost-amount" />
          </label>
          <label className="text-sm block">
            <span className="block text-xs text-muted-foreground mb-1">Incurred on</span>
            <input type="date" value={form.incurred_on} onChange={(e) => setForm({ ...form, incurred_on: e.target.value })} className="w-full bg-background border hairline px-3 py-2 text-sm" data-testid="cost-date" />
          </label>
          <label className="text-sm block">
            <span className="block text-xs text-muted-foreground mb-1">Note (optional)</span>
            <input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="invoice #" className="w-full bg-background border hairline px-3 py-2 text-sm" data-testid="cost-note" />
          </label>
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={submit} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-1" data-testid="cost-submit">
            <FloppyDisk size={12} weight="duotone" /> Log cost
          </button>
        </div>
      </div>

      <div className="card-flat p-5" data-testid="cost-log-list">
        <div className="eyebrow mb-3">Recent entries ({costs.length})</div>
        {costs.length === 0 && <div className="text-sm text-muted-foreground">No costs logged yet.</div>}
        {costs.map((c) => (
          <div key={c.id} className="flex items-center justify-between border-b hairline last:border-b-0 py-2 text-sm flex-wrap gap-2">
            <div className="min-w-0">
              <span className="font-medium">{c.category.replace(/_/g, " ")}</span>
              <span className="text-muted-foreground"> · {c.incurred_on}</span>
              {c.note && <div className="text-[10px] text-muted-foreground">{c.note}</div>}
            </div>
            <div className="flex items-center gap-2">
              <span className="num-metric text-sm">₹{Number(c.amount_inr).toLocaleString("en-IN")}</span>
              <button onClick={() => remove(c.id)} className="btn-ghost p-1.5 text-[hsl(var(--primary))]" data-testid={`cost-remove-${c.id}`}><Trash size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function KpiCard({ label, value, tone }) {
  const cls = tone === "primary" ? "text-[hsl(var(--primary))]" : tone === "secondary" ? "text-[hsl(var(--secondary))]" : "text-foreground";
  return (
    <div className="card-flat p-4">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      <div className={`num-metric text-2xl ${cls}`}>{value}</div>
    </div>
  );
}


/* -------------------- Error log (Storyboard pipeline failures) -------------------- */
const ERROR_STAGE_LABEL = {
  script:   "Claude script generation",
  image:    "Gemini image",
  video:    "Sora 2 video",
  overlay:  "ffmpeg brand overlay",
  concat:   "ffmpeg timeline concat",
  publish:  "Social publish",
  pipeline: "Pipeline crash",
};
const SEVERITY_TONE = {
  high:   { border: "border-destructive",   pill: "bg-destructive/15 text-destructive",   label: "High" },
  medium: { border: "border-[#B89B72]",      pill: "bg-[#B89B72]/20 text-[#B89B72]",        label: "Medium" },
  low:    { border: "border-muted-foreground/40", pill: "bg-muted/30 text-muted-foreground", label: "Low" },
};

function ErrorLog() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, unresolved: 0, high: 0 });
  const [filters, setFilters] = useState({ stage: "", severity: "", resolved: "false" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.stage) qs.set("stage", filters.stage);
      if (filters.severity) qs.set("severity", filters.severity);
      if (filters.resolved !== "") qs.set("resolved", filters.resolved);
      qs.set("limit", "200");
      const { data } = await api.get(`/admin/social/error-log?${qs.toString()}`);
      setItems(data.items || []);
      setCounts(data.counts || { total: 0, unresolved: 0, high: 0 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    try {
      await api.post(`/admin/social/error-log/${id}/resolve`);
      toast.success("Marked as resolved.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    try {
      await api.delete(`/admin/social/error-log/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const clearResolved = async () => {
    if (!window.confirm("Delete every already-resolved entry?")) return;
    try {
      const { data } = await api.post("/admin/social/error-log/clear-resolved");
      toast.success(`${data.deleted} resolved entr${data.deleted === 1 ? "y" : "ies"} cleared.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="storyboard-error-log">
      <div className="grid sm:grid-cols-3 gap-3">
        <KpiCard label="Unresolved errors" value={counts.unresolved} tone={counts.unresolved > 0 ? "primary" : undefined} />
        <KpiCard label="High severity (open)" value={counts.high} tone={counts.high > 0 ? "primary" : undefined} />
        <KpiCard label="Total logged (lifetime)" value={counts.total} />
      </div>

      <div className="card-flat p-4 flex items-center gap-3 flex-wrap" data-testid="error-log-filters">
        <div className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Funnel size={14} weight="duotone" /> Filters
        </div>
        <select
          value={filters.stage}
          onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
          className="bg-background border hairline px-2 py-1.5 text-xs"
          data-testid="error-filter-stage"
        >
          <option value="">All stages</option>
          {Object.entries(ERROR_STAGE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          value={filters.severity}
          onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
          className="bg-background border hairline px-2 py-1.5 text-xs"
          data-testid="error-filter-severity"
        >
          <option value="">Any severity</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select
          value={filters.resolved}
          onChange={(e) => setFilters({ ...filters, resolved: e.target.value })}
          className="bg-background border hairline px-2 py-1.5 text-xs"
          data-testid="error-filter-resolved"
        >
          <option value="false">Open only</option>
          <option value="true">Resolved only</option>
          <option value="">All</option>
        </select>
        <button
          onClick={load}
          className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1"
          data-testid="error-log-refresh"
        >
          <ArrowsClockwise size={12} weight="duotone" /> Refresh
        </button>
        <div className="ml-auto inline-flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const { data } = await api.post("/admin/social/error-log/bulk-resolve");
                toast.success(`Auto-resolved ${data.resolved} stale entries (gemini-3-pro, deleted-model probes, pre-fix JSON errors).`);
                load();
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk resolve failed");
              }
            }}
            className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1"
            data-testid="error-log-bulk-resolve"
            title="Mark all errors whose root cause has already been fixed (gemini-3-pro, deleted-model probes, pre-iter-38r2 JSON errors) as resolved."
          >
            <CheckCircle size={12} weight="duotone" /> Resolve stale
          </button>
          <button
            onClick={clearResolved}
            className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1 text-muted-foreground"
            data-testid="error-log-clear-resolved"
          >
            <Trash size={12} weight="duotone" /> Clear resolved
          </button>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card-flat p-8 text-center" data-testid="error-log-empty">
          <CheckCircle size={32} weight="duotone" className="text-[#5FB37C] mx-auto mb-3" />
          <div className="text-sm font-medium">Nothing to act on.</div>
          <div className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            Every storyboard pipeline failure (Claude script, Gemini image, Sora 2 video, ffmpeg overlay, concat, publish) lands here with a readable
            message + actionable hint. Empty list = no issues in the current filter window.
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="error-log-list">
          {items.map((it) => (
            <ErrorRow key={it.id} item={it} onResolve={resolve} onDelete={remove} />
          ))}
        </div>
      )}
    </div>
  );
}

function ErrorRow({ item, onResolve, onDelete }) {
  const tone = SEVERITY_TONE[item.severity] || SEVERITY_TONE.medium;
  const ts = new Date(item.created_at);
  const when = isNaN(ts) ? item.created_at : ts.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  const [steps, setSteps] = useState(item.suggestion_steps || null);
  const [source, setSource] = useState(item.suggestion_source || null);
  const [loading, setLoading] = useState(false);
  const fetchSuggestion = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/social/error-log/${item.id}/ai-suggest`);
      setSteps(data.steps || []);
      setSource(data.source || "ai");
      toast.success(`Suggestion ready (${data.source}).`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not fetch suggestion");
    } finally { setLoading(false); }
  };
  return (
    <div
      className={`card-flat p-4 border-l-4 ${tone.border} ${item.resolved ? "opacity-60" : ""}`}
      data-testid={`error-row-${item.id}`}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
        <div className="inline-flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${tone.pill}`}>
            {tone.label}
          </span>
          <span className="text-sm font-medium">{ERROR_STAGE_LABEL[item.stage] || item.stage}</span>
          {item.model && (
            <span className="text-[11px] text-muted-foreground">· {item.model}</span>
          )}
          {item.scene_index != null && (
            <span className="text-[11px] text-muted-foreground">· Scene #{item.scene_index}</span>
          )}
          {item.resolved && (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#5FB37C]/20 text-[#5FB37C]">
              Resolved
            </span>
          )}
        </div>
        <div className="text-[10px] text-muted-foreground">{when}</div>
      </div>
      <div className="text-sm text-foreground mb-2 break-words">{item.message}</div>
      {item.hint && (
        <div className="text-xs text-[hsl(var(--secondary))] mb-2 inline-flex items-start gap-1.5">
          <Lightbulb size={14} weight="duotone" className="shrink-0 mt-0.5" />
          <span className="break-words">{item.hint}</span>
        </div>
      )}
      {steps && steps.length > 0 && (
        <div className="card-flat p-3 mt-2 bg-[hsl(var(--primary))]/5 border-l-2 border-[hsl(var(--primary))]"
             data-testid={`error-suggestion-${item.id}`}>
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--primary))] mb-1.5">
            Corrective actions · <span className="opacity-70">{source}</span>
          </div>
          <ol className="text-xs space-y-1 list-decimal list-inside text-foreground">
            {steps.map((step, i) => (
              <li key={`${item.id}-step-${i}-${step.slice(0, 24)}`} className="break-words leading-relaxed">{step}</li>
            ))}
          </ol>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2 mt-3 pt-2 border-t hairline">
        <div className="text-[10px] text-muted-foreground space-x-3">
          <span><b>Error class:</b> {item.error_class}</span>
          {item.topic && <span className="break-words">· <b>Topic:</b> {item.topic.slice(0, 60)}{item.topic.length > 60 ? "…" : ""}</span>}
          {item.draft_id && <span>· <b>Draft:</b> {item.draft_id.slice(-6)}</span>}
        </div>
        <div className="flex items-center gap-2">
          {(!steps || steps.length === 0) && !item.resolved && (
            <button
              onClick={fetchSuggestion}
              disabled={loading}
              className="btn-ghost px-2.5 py-1 text-[11px] inline-flex items-center gap-1 disabled:opacity-40"
              data-testid={`error-suggest-${item.id}`}
            >
              <Lightbulb size={11} weight="duotone" />
              {loading ? "Thinking…" : "Suggest fix"}
            </button>
          )}
          {!item.resolved && (
            <button
              onClick={() => onResolve(item.id)}
              className="btn-ghost px-2.5 py-1 text-[11px] inline-flex items-center gap-1"
              data-testid={`error-resolve-${item.id}`}
            >
              <CheckCircle size={11} weight="duotone" /> Mark resolved
            </button>
          )}
          <button
            onClick={() => onDelete(item.id)}
            className="btn-ghost px-2.5 py-1 text-[11px] inline-flex items-center gap-1 text-muted-foreground"
            data-testid={`error-delete-${item.id}`}
          >
            <Trash size={11} weight="duotone" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// Iter-39 — Cost banner. Always-visible mini-pill showing today's spend vs cap.
// Auto-refreshes every 60s. Click jumps to the Cost dashboard sub-tab.
function CostBanner({ onClick }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data: d } = await api.get("/admin/social/cost-summary?scene_count=5");
        if (!cancelled) setData(d);
      } catch (_) { /* silent — banner is best-effort */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (!data) return null;
  const spent = Number(data.today_spent_inr || 0);
  const cap = Number(data.daily_cap_inr || 0);
  const ratio = cap > 0 ? Math.min(spent / cap, 1) : 0;
  // Color thresholds: <50% green, 50-80% amber, >80% red
  const tone = ratio >= 0.8
    ? { bar: "#D2493A", pill: "bg-[#D2493A]/15 text-[#D2493A]", border: "border-[#D2493A]/30" }
    : ratio >= 0.5
    ? { bar: "#C88A2E", pill: "bg-[#C88A2E]/15 text-[#C88A2E]", border: "border-[#C88A2E]/30" }
    : { bar: "#5FB37C", pill: "bg-[#5FB37C]/15 text-[#5FB37C]", border: "border-[#5FB37C]/30" };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="storyboard-cost-banner"
      className={`w-full card-flat p-3 border ${tone.border} hover:bg-muted/50 transition-colors text-left`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 flex-wrap">
          <Coins size={16} weight="duotone" style={{ color: tone.bar }} />
          <span className="text-sm font-medium">
            Cost today: <span style={{ color: tone.bar }}>₹{spent.toFixed(2)}</span>
            <span className="text-muted-foreground"> / ₹{cap.toFixed(0)} daily cap</span>
          </span>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${tone.pill}`}>
            {Math.round(ratio * 100)}%
          </span>
        </div>
        <span className="text-[10px] text-muted-foreground">
          Auto-refreshes every 60s · click for full breakdown
        </span>
      </div>
      <div className="mt-2 h-1.5 w-full bg-muted rounded overflow-hidden">
        <div
          className="h-full transition-all"
          style={{ width: `${ratio * 100}%`, backgroundColor: tone.bar }}
          data-testid="storyboard-cost-banner-bar"
        />
      </div>
    </button>
  );
}



// Iter-40 round 2 — Library usage pill (sits next to the Cost banner).
// Auto-refreshes every 60s. When ratio ≥ 0.8 a one-click Auto-prune appears.
function LibraryUsagePill({ onClick }) {
  const [data, setData] = useState(null);
  const [pruning, setPruning] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/social/library/usage");
      setData(d);
    } catch (_) { /* silent */ }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  if (!data) return null;
  const ratio = Number(data.ratio || 0);
  const usedMb = Number(data.used_mb || 0);
  const capMb = Number(data.cap_mb || 0);
  const tone = ratio >= 0.8
    ? { bar: "#D2493A", pill: "bg-[#D2493A]/15 text-[#D2493A]", border: "border-[#D2493A]/30" }
    : ratio >= 0.5
    ? { bar: "#C88A2E", pill: "bg-[#C88A2E]/15 text-[#C88A2E]", border: "border-[#C88A2E]/30" }
    : { bar: "#5FB37C", pill: "bg-[#5FB37C]/15 text-[#5FB37C]", border: "border-[#5FB37C]/30" };
  const prune = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Auto-prune will delete the oldest non-pinned library entries until usage is at or below 50% of the ${capMb} MB cap. Pinned entries are never deleted. Continue?`)) return;
    setPruning(true);
    try {
      const { data: r } = await api.post("/admin/social/library/auto-prune");
      toast.success(`Pruned ${r.deleted} entries, freed ${r.freed_mb} MB.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Prune failed");
    } finally { setPruning(false); }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="storyboard-library-pill"
      className={`w-full card-flat p-3 border ${tone.border} hover:bg-muted/50 transition-colors text-left`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 flex-wrap">
          <BookmarkSimple size={16} weight="duotone" style={{ color: tone.bar }} />
          <span className="text-sm font-medium">
            Library: <span style={{ color: tone.bar }}>{usedMb} MB</span>
            <span className="text-muted-foreground"> / {capMb} MB cap</span>
            <span className="text-muted-foreground"> · {data.count} {data.count === 1 ? "entry" : "entries"}</span>
          </span>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${tone.pill}`}>
            {Math.round(ratio * 100)}%
          </span>
        </div>
        {ratio >= 0.8 && (
          <button
            onClick={prune}
            disabled={pruning}
            className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-[#D2493A] text-white hover:bg-[#B5392A] disabled:opacity-50"
            data-testid="storyboard-library-autoprune"
            title="Delete the oldest non-pinned entries until usage is ≤50% of cap"
          >
            {pruning ? "Pruning…" : "Auto-prune"}
          </button>
        )}
      </div>
      <div className="mt-2 h-1.5 w-full bg-muted rounded overflow-hidden">
        <div
          className="h-full transition-all"
          style={{ width: `${Math.min(ratio, 1) * 100}%`, backgroundColor: tone.bar }}
          data-testid="storyboard-library-pill-bar"
        />
      </div>
    </button>
  );
}

// Iter-40 round 2 — Library sub-tab. Browse + curate saved assets.
function LibraryPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all"); // all | script | image | video
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === "all"
        ? "/admin/social/library"
        : `/admin/social/library?kind=${filter}`;
      const { data } = await api.get(url);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Library load failed");
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const togglePin = async (id, next) => {
    try {
      await api.put(`/admin/social/library/${id}/pin`, { pinned: next });
      setItems((xs) => xs.map((x) => x.id === id ? { ...x, pinned: next } : x));
      toast.success(next ? "Pinned." : "Unpinned.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const rename = async (entry) => {
    const next = window.prompt("New name:", entry.name);
    if (!next || next === entry.name) return;
    try {
      await api.put(`/admin/social/library/${entry.id}`, {
        name: next, tags: entry.tags, notes: entry.notes,
      });
      setItems((xs) => xs.map((x) => x.id === entry.id ? { ...x, name: next } : x));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const remove = async (entry) => {
    if (entry.pinned && !window.confirm(`"${entry.name}" is pinned. Delete anyway?`)) return;
    if (!entry.pinned && !window.confirm(`Delete "${entry.name}"?`)) return;
    try {
      await api.delete(`/admin/social/library/${entry.id}`);
      setItems((xs) => xs.filter((x) => x.id !== entry.id));
      toast.success("Deleted.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="storyboard-library-panel">
      <div className="card-flat p-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow mb-1">Asset library</div>
          <p className="text-xs text-muted-foreground max-w-xl">
            Saved scripts, images, and videos for future re-use. Pin the ones
            you want protected from auto-prune. Apply back to any scene from
            the composer using the saved assets.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowUpload(true)}
            className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
            title="Upload images / videos / scripts from desktop or import via a Google Drive share link."
            data-testid="library-upload-open">
            <UploadSimple size={12} weight="bold" /> Upload assets
          </button>
          <div className="inline-flex rounded border hairline overflow-hidden" data-testid="library-filter">
            {[["all", "All"], ["script", "Scripts"], ["image", "Images"], ["video", "Videos"]].map(([id, lbl]) => (
              <button
                key={id}
                onClick={() => setFilter(id)}
                data-testid={`library-filter-${id}`}
                className={`px-3 py-1.5 text-xs ${filter === id ? "bg-[hsl(var(--primary))] text-white" : "bg-background text-muted-foreground"} ${id !== "all" ? "border-l hairline" : ""}`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
      </div>

      {showUpload && (
        <LibraryUploadModal
          onClose={() => setShowUpload(false)}
          onUploaded={() => { load(); setShowUpload(false); }}
        />
      )}

      {loading ? (
        <div className="text-xs text-muted-foreground p-8 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card-flat p-8 text-center text-xs text-muted-foreground" data-testid="library-empty">
          No {filter === "all" ? "" : filter} assets saved yet. From the Workflow composer,
          click the bookmark icon on any scene to save it here.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((entry) => (
            <LibraryCard key={entry.id} entry={entry}
              onPin={togglePin} onRename={rename} onDelete={remove} />
          ))}
        </div>
      )}
    </div>
  );
}

function LibraryCard({ entry, onPin, onRename, onDelete }) {
  const kindIcon = entry.kind === "script" ? Sparkle
                 : entry.kind === "image"  ? ImageIcon
                 : FilmStrip;
  const KindIcon = kindIcon;
  return (
    <div
      className={`card-flat p-3 space-y-2 ${entry.pinned ? "border-2 border-[hsl(var(--primary))]" : ""}`}
      data-testid={`library-card-${entry.id}`}
    >
      {/* Preview */}
      {entry.kind === "image" && entry.asset_url && (
        <img src={assetUrl(entry.asset_url)} alt={entry.name}
          className="w-full aspect-video object-cover border hairline" />
      )}
      {entry.kind === "video" && entry.asset_url && (
        <video
          src={assetUrl(entry.asset_url)}
          controls
          aria-label={`Saved video: ${entry.name}`}
          className="w-full aspect-video bg-black border hairline"
        >
          <track kind="captions" />
        </video>
      )}
      {entry.kind === "script" && (
        <div className="text-[11px] text-muted-foreground line-clamp-3 italic">
          “{entry.preview || "(no preview)"}”
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate inline-flex items-center gap-1">
            <KindIcon size={12} weight="duotone" className="shrink-0" />
            {entry.name}
            {entry.pinned && (
              <span title="Pinned — immune to auto-prune">
                <PushPin size={11} weight="fill" className="text-[hsl(var(--primary))]" />
              </span>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground">
            {entry.kind}
            {entry.image_kind && ` · ${entry.image_kind}`}
            {entry.size_bytes ? ` · ${(entry.size_bytes / 1_048_576).toFixed(2)} MB` : ""}
            {entry.source_scene_index ? ` · Scene #${entry.source_scene_index}` : ""}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 pt-1 border-t hairline">
        <button
          onClick={() => onPin(entry.id, !entry.pinned)}
          className="btn-ghost px-2 py-1 text-[10px] inline-flex items-center gap-1"
          data-testid={`library-pin-${entry.id}`}
          title={entry.pinned ? "Unpin" : "Pin (immune to auto-prune)"}
        >
          <PushPin size={10} weight={entry.pinned ? "fill" : "duotone"} />
          {entry.pinned ? "Unpin" : "Pin"}
        </button>
        <button
          onClick={() => onRename(entry)}
          className="btn-ghost px-2 py-1 text-[10px]"
          data-testid={`library-rename-${entry.id}`}
        >
          Rename
        </button>
        <button
          onClick={() => onDelete(entry)}
          className="btn-ghost px-2 py-1 text-[10px] text-destructive ml-auto inline-flex items-center gap-1"
          data-testid={`library-delete-${entry.id}`}
        >
          <Trash size={10} weight="duotone" /> Delete
        </button>
      </div>
    </div>
  );
}

